"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CDNImage } from "@/components/ui/cdn-image";
import { HeaderSearch } from "@/components/header-search";
import { ThemeToggle } from "@/components/theme-toggle";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Browse Brands", href: "/browse" },
  { name: "Categories", href: "/categories" },
  { name: "Companies", href: "/companies" },
  { name: "Compare", href: "/compare" },
  { name: "Blog", href: "/blog" },
  { name: "Quiz", href: "/quiz" },
  { name: "About", href: "/about" },
];

function isActive(pathname: string, href: string): boolean {
  if (href === "/") {
    return pathname === "/";
  }
  return pathname === href || pathname.startsWith(href + "/");
}

export function Header() {
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const iconSrc = mounted && resolvedTheme === "dark" ? "/icon-dark-32x32.png" : "/icon-light-32x32.png";

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 flex-shrink-0">
            <CDNImage 
              src={iconSrc} 
              alt="Who Brands - Brand Ownership Directory" 
              width={40}
              height={40}
              className="h-10 w-10"
              priority
            />
            <span className="text-xl font-semibold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Who Brands
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:gap-1 flex-1 justify-center">
            {navigation.map((item) => {
              const active = isActive(pathname, item.href);
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`relative px-4 py-2 text-base font-medium transition-colors rounded-md ${
                    active
                      ? "text-accent bg-accent/10 dark:text-black dark:bg-white/80"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted dark:text-white dark:hover:text-white"
                  }`}
                >
                  {item.name}
                  {active && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-6 bg-accent rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Desktop Search + Theme Toggle */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <HeaderSearch />
            <ThemeToggle />
          </div>

          {/* Mobile theme toggle + menu button */}
          <div className="flex items-center gap-1 md:hidden flex-shrink-0">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            mobileMenuOpen
              ? "max-h-96 opacity-100 border-t border-border"
              : "max-h-0 opacity-0 border-t border-transparent"
          }`}
        >
          <div className="py-4">
            <div className="flex flex-col space-y-1">
              <div className="px-4 pb-3">
                <HeaderSearch />
              </div>
              {navigation.map((item) => {
                const active = isActive(pathname, item.href);
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`px-4 py-3 text-xl font-medium rounded-md transition-colors ${
                      active
                        ? "text-accent bg-accent/10 dark:text-black dark:bg-white/80"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted dark:text-white dark:hover:text-white"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
